import { useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUILabels } from '../../context/UILabelsContext';
import { HyreDocsIcon } from '../HyreDocsLogo';
import {
  LayoutDashboard,
  FileText,
  Send,
  ClipboardList,
  ClipboardEdit,
  Settings,
  LogOut,
  Building2,
  Inbox,
} from 'lucide-react';

// Workspace = which product the recruiter is using. The sidebar
// switches its entire nav list when this changes. Two products today:
//   • documents — PDF templates + Send + Documents tracking
//   • forms     — Online Forms builder + Submissions tracking
// The choice is derived from the URL (forms paths → forms mode) and
// persisted to localStorage so refreshing the dashboard remembers the
// last-used product.
const WORKSPACE_DOCS = 'documents';
const WORKSPACE_FORMS = 'forms';
const WORKSPACE_STORAGE_KEY = 'hyredocs.workspace';

function inferWorkspaceFromPath(pathname) {
  // `/<tenant>/forms` and `/<tenant>/forms/...` → Forms workspace.
  // Everything else stays in Documents — that's the safer default for
  // existing customers that aren't on the Forms package.
  return /\/forms(\/|$)/.test(pathname) ? WORKSPACE_FORMS : WORKSPACE_DOCS;
}

export const Sidebar = () => {
  const { user, tenant, logout, getTenantPath } = useAuth();
  const uiLabels = useUILabels();
  const navigate = useNavigate();
  const location = useLocation();

  const docsEnabled = tenant?.settings?.enable_documents !== false; // default true
  const formsEnabled = !!tenant?.settings?.enable_online_forms;
  const isAdmin = user?.role === 'admin' || user?.is_master_admin;

  // Decide which workspace is "active" right now. Priority:
  //   1. URL pathname is the source of truth — if you're on /forms/* you're
  //      in Forms mode, period.
  //   2. Otherwise honour the last-used choice from localStorage.
  //   3. Otherwise default to whichever product is enabled (preferring docs
  //      since that's the legacy product and the safer default).
  const urlWorkspace = inferWorkspaceFromPath(location.pathname);
  const lastUsed = (typeof window !== 'undefined' && localStorage.getItem(WORKSPACE_STORAGE_KEY)) || null;
  let workspace = urlWorkspace;
  if (urlWorkspace === WORKSPACE_DOCS && lastUsed === WORKSPACE_FORMS && formsEnabled && !docsEnabled) {
    workspace = WORKSPACE_FORMS;
  }
  if (workspace === WORKSPACE_FORMS && !formsEnabled) workspace = WORKSPACE_DOCS;
  if (workspace === WORKSPACE_DOCS && !docsEnabled && formsEnabled) workspace = WORKSPACE_FORMS;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const switchWorkspace = (next) => {
    if (typeof window !== 'undefined') localStorage.setItem(WORKSPACE_STORAGE_KEY, next);
    // Send the recruiter to a workspace-specific landing page so the pill
    // switch actually changes context. Dashboard is now overarching and
    // is accessed via the persistent Dashboard nav link above the pills.
    if (next === WORKSPACE_FORMS) {
      navigate(getTenantPath('/forms'));
    } else {
      navigate(getTenantPath('/templates'));
    }
  };

  const navItems = useMemo(() => {
    // Dashboard is now rendered ABOVE the workspace switcher pills (it's
    // a unified, workspace-agnostic view), so it's NOT included here.
    // These items are the per-workspace ones that flip when the user
    // toggles the pills.
    const items = [];
    if (workspace === WORKSPACE_FORMS) {
      items.push(
        { to: getTenantPath('/forms'), icon: ClipboardEdit, label: uiLabels.forms },
        { to: getTenantPath('/forms/submissions'), icon: Inbox, label: 'Submissions' },
      );
    } else {
      items.push(
        { to: getTenantPath('/templates'), icon: FileText, label: uiLabels.templates },
        { to: getTenantPath('/send'), icon: Send, label: uiLabels.send },
        { to: getTenantPath('/documents'), icon: ClipboardList, label: uiLabels.documents },
      );
    }
    if (isAdmin) {
      items.push({ to: getTenantPath('/settings'), icon: Settings, label: 'Settings' });
    }
    return items;
  }, [workspace, isAdmin, getTenantPath, uiLabels]);

  // Only show the workspace switcher when the tenant actually has both
  // products enabled. Single-product tenants get a clean sidebar.
  const showWorkspaceSwitcher = docsEnabled && formsEnabled;

  return (
    <aside className="w-64 bg-white border-r border-border h-screen flex flex-col" data-testid="sidebar">
      {/* Logo & Tenant */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-md shadow-orange-500/20">
            <HyreDocsIcon className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-lg text-foreground">HyreDocs</h1>
            <p className="text-xs text-muted-foreground">Bullhorn Integration</p>
          </div>
        </div>

        {/* Tenant Badge */}
        {tenant && (
          <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{tenant.name}</p>
                <p className="text-xs text-muted-foreground">/{tenant.slug}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Persistent overarching Dashboard link — sits ABOVE the workspace
          pill switcher because the dashboard is now a single unified view
          covering both Documents and Forms. */}
      <div className="px-4 pt-4" data-testid="sidebar-dashboard-block">
        <NavLink
          to={getTenantPath('/dashboard')}
          end
          className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
          data-testid="nav-dashboard"
        >
          <LayoutDashboard className="w-5 h-5" />
          <span className="font-body">Dashboard</span>
        </NavLink>
      </div>

      {/* Workspace switcher — only when both products are licensed */}
      {showWorkspaceSwitcher && (
        <div className="px-4 pt-3" data-testid="workspace-switcher">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 px-1">Workspace</p>
          <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-lg">
            <WorkspacePill
              active={workspace === WORKSPACE_DOCS}
              onClick={() => switchWorkspace(WORKSPACE_DOCS)}
              icon={FileText}
              label={uiLabels.documents}
              testId="workspace-pill-documents"
            />
            <WorkspacePill
              active={workspace === WORKSPACE_FORMS}
              onClick={() => switchWorkspace(WORKSPACE_FORMS)}
              icon={ClipboardEdit}
              label={uiLabels.forms}
              testId="workspace-pill-forms"
            />
          </div>
        </div>
      )}

      {/* Navigation (workspace-specific items) */}
      <nav className="flex-1 px-4 pt-2 pb-4 space-y-1" data-testid={`nav-${workspace}`}>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-body">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="font-heading font-semibold text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-body font-medium text-sm truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
};

function WorkspacePill({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      className={`flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
        active
          ? 'bg-white text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground hover:bg-white/60'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
